import React, { useCallback } from 'react';
import { Button } from 'src/components/ui/button';
import { Sparkles, FileText, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'src/hooks/use-toast';
import { UserService } from 'src/services/api/UserService';
import { jwtDecode } from 'jwt-decode';
import {
  getRefreshToken,
  setToken,
  setRefreshToken,
  setUser,
} from 'src/utils/authStorage';

export function Dashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();

  /* ── Refresh token & persist updated user info ── */
  const refreshAndSaveUser = useCallback(async () => {
    try {
      const storedRefreshToken = getRefreshToken();
      if (!storedRefreshToken) return;
      const res = await UserService.refreshToken(storedRefreshToken);
      const tokens = res?.data?.data?.tokens || res?.data?.tokens;
      if (!tokens?.access_token) return;
      setToken(tokens.access_token);
      if (tokens.refresh_token) setRefreshToken(tokens.refresh_token);
      try {
        const decoded = jwtDecode(tokens.access_token);
        const updatedUser = {
          cid: decoded?.cid,
          email: decoded?.email,
          firstName: decoded?.given_name,
          lastName: decoded?.family_name,
          emailVerified: decoded?.email_verified,
          roles: decoded?.realm_access?.roles,
          secAuth: decoded?.secAuth,
          secondaryAuth: decoded?.secondaryAuth,
          ack: decoded?.ack,
        };
        setUser(updatedUser);
      } catch (decodeErr) {
        console.error('Failed to decode refreshed token:', decodeErr);
      }
    } catch (err) {
      console.error('Failed to refresh token after acknowledge intro:', err);
    }
  }, []);

  const steps = [
    {
      title: 'Complete Your Profile',
      icon: '1',
      description:
        'Add your education, experience, skills, and certifications. Takes ~2 minutes.',
    },
    {
      title: 'Quick Assessment & Micro-Bootcamp',
      icon: '2',
      description:
        '5-min skills check, then a bite-size bootcamp on AML/KYC basics.',
    },
    {
      title: 'Get Matched & Launch Your Career',
      icon: '3',
      description:
        "We'll match you to roles at leading banks & fintechs—interview and get hired.",
    },
  ];

  return (
    <div className="min-h-full bg-gradient-to-b from-blue-50 via-white to-blue-50">
      <div className="container mx-auto px-4 py-8 pb-24 md:pb-8 max-w-6xl">
        <div>
          <h1
            data-source-pos="183:8-189:20"
            data-source-name="motion.h1"
            id="main"
            className="text-center text-3xl md:text-4xl font-extrabold tracking-tight text-blue-700 leading-[1.1] max-w-3xl mx-auto"
            style={{ opacity: 1, transform: 'none' }}
          >
            Welcome to AHEDGE People Services!
          </h1>
          <div
            data-source-pos="190:8-196:10"
            data-source-name="motion.div"
            aria-hidden="true"
            className="mx-auto mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-blue-600 via-cyan-400 to-indigo-500"
            style={{ transform: 'none', transformOrigin: '0% 50% 0px' }}
          >
            {}
          </div>
          <p className="text-center text-gray-600 mb-10 mt-4 text-sm">
            Your guided pathway to a thriving career in Risk & Compliance.
          </p>
          {/* Stepper Section */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className="bg-white border border-blue-100 rounded-xl p-6 text-left shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg border bg-card text-card-foreground h-full flex flex-col md:min-h-[200px] border-blue-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex gap-3 mb-4">
                  <div className="flex px-2 text-xs items-center justify-center bg-gray-100 text-gray-600 rounded-full flex items-center justify-center font-semibold text-xs">
                    step {step.icon}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-start gap-4">
                  <span className="text-blue-600 bg-blue-50 rounded-md flex items-center justify-center p-4">
                    {index === 0 && (
                      <FileText className="w-5 h-5 text-blue-600" />
                    )}
                    {index === 1 && (
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    )}
                    {index === 2 && (
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    )}
                  </span>
                  {step.title}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
          {/* Button */}
          <div className="text-center mt-6">
            <Button
              onClick={async () => {
                try {
                  await UserService.acknowledgeIntro();
                  // Refresh token to get updated user info with new ack value
                  await refreshAndSaveUser();
                  navigate('/profile-dashboard');
                } catch (error) {
                  console.error('Failed to acknowledge intro:', error);
                  toast({
                    message:
                      'Something went wrong. Unable to continue. Please try again.',
                    variant: 'destructive',
                  });
                }
              }}
              className="bg-ah-brand-primary hover:bg-ah-brand-primary-hover"
            >
              Complete Profile
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              <span className="inline-flex items-center">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Takes ~2 minutes.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
